/**
 * Tier-specific concierge rewards, sourced from `data/conciergePartners.js`.
 * Every image tries the local file in /public/assets/images/concierge/… first
 * and falls back to a verified Pexels URL if the local file is not present.
 * Fuel is a single generic tile — text list of accepted stations, no
 * per-brand imagery.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import {
  Lock, CheckCircle2, MapPin, X, Loader2, ChevronRight,
  Fuel, ShoppingBag, Sparkles, Wrench, BedDouble, TreePine,
} from "lucide-react";
import { CONCIERGE } from "../../data/conciergePartners";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map the terminal's per-tier item key → concierge category
const CATEGORY = {
  fuel:       CONCIERGE.fuel,
  fuel_full:  CONCIERGE.fuel,
  grocery:    CONCIERGE.grocery_voucher,
  wash:       CONCIERGE.car_wash,
  service:    CONCIERGE.car_service,
  hotel:      CONCIERGE.hotel_stay,
  safari:     CONCIERGE.game_park_visit,
};
const CATEGORY_ICON = {
  fuel: Fuel, fuel_full: Fuel, grocery: ShoppingBag, wash: Sparkles,
  service: Wrench, hotel: BedDouble, safari: TreePine,
};

// ── Photo tile: local-first, fallback URL, Lucide icon last ────
function PhotoTile({ localSrc, fallbackSrc, tint, label, sublabel, Icon }) {
  // 0 = try local, 1 = try fallback, 2 = icon
  const [stage, setStage] = useState(0);
  const src = stage === 0 ? localSrc : (stage === 1 ? fallbackSrc : null);
  return (
    <div className="relative h-full w-full overflow-hidden bg-ink">
      {src ? (
        <img
          key={stage}
          src={src}
          alt={label || ""}
          loading="lazy"
          onError={() => setStage((s) => s + 1)}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        Icon && (
          <div className="absolute inset-0 flex items-center justify-center"
               style={{ background: `linear-gradient(135deg, ${tint} 0%, #000 100%)` }}>
            <Icon className="h-14 w-14 text-white/90" strokeWidth={1.3} />
          </div>
        )
      )}
      <div className="absolute inset-0" style={{
        background: `linear-gradient(180deg, ${tint}30 0%, transparent 35%, #000000AA 100%)`,
      }} />
      {label && (
        <div className="absolute inset-x-0 bottom-0 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.95)]">
            {label}
          </p>
          {sublabel && (
            <p className="mt-0.5 font-tabular text-[10px] uppercase tracking-[0.2em] text-gold drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
              {sublabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Per-tier voucher specs ─────────────────────────────────────
const TIER_BENEFITS = {
  100: {
    title: "Bronze",
    items: [
      { key: "grocery", title: "Grocery voucher", value: "R 800" },
      { key: "fuel",    title: "Fuel voucher",    value: "8 L" },
      { key: "wash",    title: "Car wash",        value: "Complimentary" },
    ],
    cycle: "Every 30 days from signup",
  },
  250: {
    title: "Silver",
    items: [
      { key: "grocery", title: "Grocery voucher", value: "R 1 000" },
      { key: "fuel",    title: "Fuel voucher",    value: "10 L" },
      { key: "wash",    title: "Car wash",        value: "Complimentary" },
    ],
    cycle: "Every 30 days from signup",
  },
  500: {
    title: "Gold",
    items: [
      { key: "fuel_full", title: "Full fuel tank",  value: "Redeem once" },
      { key: "grocery",   title: "Grocery voucher", value: "R 1 200" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  1000: {
    title: "Black",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 1 200" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "Curated partners" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  2000: {
    title: "Platinum",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 2 000" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "2 nights · curated" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  3000: {
    title: "Sapphire",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 2 500" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "2 nights · premium" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  4000: {
    title: "Ruby",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 3 000" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "3 nights · premium" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  5000: {
    title: "Diamond",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 4 000" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "3 nights · luxury" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  8000: {
    title: "Emerald",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 5 000" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "4 nights · luxury" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  10000: {
    title: "Executive VIP",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 7 500" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "5 nights · executive" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
  20000: {
    title: "Diplomatic",
    items: [
      { key: "grocery",   title: "Grocery voucher", value: "R 10 000" },
      { key: "fuel_full", title: "Full fuel tank",  value: "Complimentary" },
      { key: "hotel",     title: "Hotel stay",      value: "7 nights · flagship" },
      { key: "safari",    title: "Game park visit", value: "Complimentary" },
      { key: "wash",      title: "Car wash",        value: "Complimentary" },
      { key: "service",   title: "Car service",     value: "Complimentary" },
    ],
    cycle: "First unlock 30 days after signup",
  },
};

// ── Benefit tile on the main card ──────────────────────────────
function VoucherTile({ item, unlocked, onSelect, alreadyRedeemed }) {
  const cat = CATEGORY[item.key];
  const Icon = CATEGORY_ICON[item.key];
  return (
    <button
      type="button"
      onClick={() => unlocked && onSelect(item)}
      disabled={!unlocked}
      className={`group relative flex flex-col overflow-hidden rounded-lg border text-left transition-all ${
        unlocked ? "border-gold/40 hover:border-gold hover:shadow-[0_0_24px_rgba(212,175,55,0.15)] cursor-pointer" : "border-white/10 opacity-70 cursor-not-allowed"
      }`}
      data-testid={`voucher-${item.key}`}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        <PhotoTile
          localSrc={cat.hero.image_asset}
          fallbackSrc={cat.hero.fallback}
          tint={cat.tint}
          label={item.title}
          sublabel={item.value}
          Icon={Icon}
        />
        <div className="absolute right-3 top-3">
          {alreadyRedeemed ? (
            <span className="flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[9px] uppercase tracking-[0.2em] text-[#26A69A] backdrop-blur-sm">
              <CheckCircle2 className="h-3 w-3" /> Chosen
            </span>
          ) : unlocked ? (
            <span className="flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[9px] uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
              Tap to choose <ChevronRight className="h-3 w-3" />
            </span>
          ) : (
            <span className="flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-[9px] uppercase tracking-[0.2em] text-white/70 backdrop-blur-sm">
              <Lock className="h-3 w-3" /> Day 30
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Picker modal ──────────────────────────────────────────────
function PartnerPicker({ item, onClose, onConfirmed, token }) {
  const cat = CATEGORY[item.key];
  const Icon = CATEGORY_ICON[item.key];
  const [chosen, setChosen] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Build a single text-pill list from either the fuel `stations` field or the
  // partner catalogue — every category now uses the same "one hero wallpaper +
  // partner name pills" pattern for a consistent, uncluttered concierge feel.
  const options = cat.stations
    ? cat.stations.map((s) => ({ id: s.toLowerCase().replace(/\W+/g, "_"), name: s }))
    : (cat.partners || []).map((p) => ({ id: p.id, name: p.name }));
  const description = cat.description || `Select your preferred ${cat.display_name.toLowerCase()}`;

  const submit = async () => {
    if (!chosen) return;
    setBusy(true); setErr(null);
    try {
      await axios.post(
        `${API}/terminal/benefits/redeem`,
        { benefit_key: item.key, benefit_title: item.title,
          value: item.value, partner: chosen, note: note.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onConfirmed(chosen);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not save your choice.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" data-testid="benefit-picker-modal">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto border border-gold/30 bg-ink shadow-[0_0_60px_rgba(212,175,55,0.15)]">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <p className="text-[9px] uppercase tracking-[0.28em] text-gold">Concierge · pick a partner</p>
            <h3 className="mt-1 font-serif text-2xl font-light text-white" data-testid="benefit-picker-title">
              {item.title} · <span className="text-gold">{item.value}</span>
            </h3>
          </div>
          <button type="button" onClick={onClose}
            className="rounded-full border border-white/15 p-2 text-white/60 hover:border-gold hover:text-gold"
            data-testid="benefit-picker-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Themed wallpaper describing the whole offer ── */}
        <div className="p-5">
          <div className="relative aspect-[16/9] w-full overflow-hidden rounded-md">
            <PhotoTile
              localSrc={cat.hero.image_asset}
              fallbackSrc={cat.hero.fallback}
              tint={cat.tint}
              Icon={Icon}
            />
            <div className="absolute inset-x-0 bottom-0 p-4">
              <p className="text-[10px] uppercase tracking-[0.28em] text-gold drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                {cat.display_name}
              </p>
              <p className="mt-1 font-serif text-xl text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                {item.title} · <span className="text-gold">{item.value}</span>
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-white/85 drop-shadow-[0_1px_3px_rgba(0,0,0,0.95)]">
                {description}
              </p>
            </div>
          </div>

          <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-white/45">
            Preferred partner
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setChosen(o.name)}
                data-testid={`partner-option-${o.id}`}
                className={`rounded-full border px-4 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  chosen === o.name
                    ? "border-gold bg-gold/10 text-gold shadow-[0_0_12px_rgba(212,175,55,0.25)]"
                    : "border-white/15 text-white/70 hover:border-gold/60 hover:text-gold"
                }`}
              >
                {o.name}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 px-5 py-4">
          <p className="mb-3 text-[10px] uppercase tracking-[0.22em] text-white/45">
            Aureon is not affiliated with the businesses listed. Names indicate your preferred redemption partner within the category — concierge fulfils via voucher.
          </p>
          <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-white/40" htmlFor="benefit-note">
            Note for the concierge (optional)
          </label>
          <input
            id="benefit-note"
            data-testid="benefit-picker-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. preferred branch, dates, dietary needs"
            maxLength={140}
            className="w-full border border-white/15 bg-surface px-4 py-2.5 font-tabular text-xs text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
          />
          {err && <p className="mt-2 text-[11px] text-[#FF003C]" data-testid="benefit-picker-error">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="rounded-full border border-white/15 px-5 py-2 text-[10px] uppercase tracking-[0.2em] text-white/60 hover:border-white/40">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={!chosen || busy}
              data-testid="benefit-picker-confirm"
              className="flex items-center gap-2 rounded-full bg-gold px-6 py-2 text-[10px] uppercase tracking-[0.2em] text-ink hover:bg-gold-light disabled:opacity-40">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Confirm {chosen ? `· ${chosen}` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MembershipBenefits({ tier = 100, createdAt, token }) {
  const spec = TIER_BENEFITS[tier] || TIER_BENEFITS[100];
  const [selected, setSelected] = useState(null);
  const [redeemed, setRedeemed] = useState({});
  const [toast, setToast] = useState(null);
  const [cycle, setCycle] = useState({ selected: [], limit: 2, remaining: 2, unlocked: false });

  // Load this cycle's picks so 'X / 2' persists across sessions.
  const refreshCycle = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/terminal/benefits/mine`,
        { headers: { Authorization: `Bearer ${token}` } });
      setCycle(data);
      const map = {};
      (data.selected || []).forEach((r) => { map[r.benefit_key] = r.partner; });
      setRedeemed(map);
    } catch { /* silent — tile still works */ }
  };
  useMemo(() => { refreshCycle(); /* eslint-disable-next-line */ }, [token]);

  const { unlocked, daysUntil, cyclePct } = useMemo(() => {
    if (!createdAt) return { unlocked: false, daysUntil: 30, cyclePct: 0 };
    const created = new Date(createdAt).getTime();
    if (!Number.isFinite(created)) return { unlocked: false, daysUntil: 30, cyclePct: 0 };
    const now = Date.now();
    const daysSince = (now - created) / (1000 * 60 * 60 * 24);
    const cycle = daysSince % 30;
    return {
      unlocked: daysSince >= 30,
      daysUntil: Math.max(0, Math.ceil(30 - cycle)),
      cyclePct: Math.min(100, (cycle / 30) * 100),
    };
  }, [createdAt]);

  const onConfirmed = (partner) => {
    setRedeemed((r) => ({ ...r, [selected.key]: partner }));
    setToast(`✓ ${selected.title} · ${partner} — concierge notified.`);
    setSelected(null);
    setTimeout(() => setToast(null), 5000);
    refreshCycle();
  };

  const atLimit = cycle.unlocked && (cycle.remaining ?? 2) <= 0;
  const openSelect = (item) => {
    if (redeemed[item.key]) return;
    if (atLimit) {
      setToast(`✕ You've already picked your ${cycle.limit} benefits for this cycle.`);
      setTimeout(() => setToast(null), 4200);
      return;
    }
    setSelected(item);
  };

  return (
    <div className="border border-gold/20 bg-surface p-6" data-testid="membership-benefits">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Membership benefits</p>
          <p className="mt-1 font-serif text-2xl font-light text-white">
            {spec.title} · <span className="text-gold">Concierge rewards</span>
          </p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{spec.cycle}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">30-day cycle</p>
          <p className="font-tabular text-sm text-gold" data-testid="benefits-days-until">
            {unlocked ? `${daysUntil} day(s) to next reload` : `Unlocks in ${daysUntil} day(s)`}
          </p>
          <p className="mt-1 font-tabular text-[11px] uppercase tracking-[0.2em] text-white/60" data-testid="benefits-picks-remaining">
            Picks this cycle · <span className={atLimit ? "text-[#FF003C]" : "text-gold"}>
              {(cycle.limit ?? 2) - (cycle.remaining ?? 2)}
            </span>
            <span className="text-white/40"> / {cycle.limit ?? 2}</span>
          </p>
        </div>
      </div>

      <div className="mt-4 h-1.5 w-full overflow-hidden bg-white/[0.05]">
        <div className="h-full bg-gradient-to-r from-gold/40 to-gold transition-all"
             style={{ width: `${cyclePct}%` }} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {spec.items.map((it) => (
          <VoucherTile
            key={it.key}
            item={it}
            unlocked={unlocked && (!atLimit || !!redeemed[it.key])}
            onSelect={openSelect}
            alreadyRedeemed={!!redeemed[it.key]}
          />
        ))}
      </div>

      <p className="mt-5 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/40">
        <MapPin className="h-3 w-3" />
        Redeemable at partner stores in South Africa · concierge confirms every choice within 24 h
      </p>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm rounded border border-[#00FF41]/40 bg-ink px-4 py-3 text-[11px] text-[#00FF41] shadow-[0_0_24px_rgba(0,255,65,0.15)]" data-testid="benefit-toast">
          {toast}
        </div>
      )}

      {selected && (
        <PartnerPicker
          item={selected}
          onClose={() => setSelected(null)}
          onConfirmed={onConfirmed}
          token={token}
        />
      )}
    </div>
  );
}
